<template>
    <div
        class="py-3 overflow-x-auto scrollbar-none"
        data-cy="filters-bar"
    >
        <div class="px-3 inline-flex w-max space-x-2 h-8">
            <DButton
                v-if="filters.length > 1"
                class="shrink-0 cursor-pointer"
                size="sm"
                :theme="activeFilterCount ? 'outline' : 'secondary'"
                data-cy="filters-bar-filters"
                @click="handleFilters"
            >
                <PhIcon
                    icon="sliders-horizontal"
                    :size="15"
                    weight="bold"
                    class="inline-block mr-1"
                />
                Filter
                <DBadge
                    v-if="activeFilterCount"
                    type="base-invert"
                    class="ml-1 -mr-1"
                    rounded
                >
                    {{ activeFilterCount }}
                </DBadge>
            </DButton>

            <DButton
                v-for="item in barItems"
                :key="item.id"
                class="shrink-0 cursor-pointer"
                size="sm"
                :theme="item.active ? 'outline' : 'secondary'"
                :data-cy="item.active ? 'filters-bar-filter-active' : 'filters-bar-filter-inactive'"
                @click="item.onClick"
            >
                {{ item.title }}
                <template v-if="item.count">
                    <DBadge
                        v-if="item.count"
                        type="base-invert"
                    >
                        {{ item.count }}
                    </DBadge>
                    <PhIcon
                        icon="caret-down"
                        :size="15"
                        weight="bold"
                        class="inline-block ml-1"
                    />
                </template>
                <template v-else>
                    <PhIcon
                        :icon="item.active ? 'x' : 'caret-down'"
                        :size="15"
                        weight="bold"
                        class="inline-block ml-1"
                    />
                </template>
            </DButton>
        </div>
    </div>
</template>

<script setup>
    import {
        flatMap, keys, get,
    } from 'lodash'
    import { DButton, DBadge } from '@digistorm/spark'

    import PhIcon from '@/components/app/PhIcon.vue'
    import { useFilters } from '@/composables'
    import { useFilterStore } from '@/stores/filter'
    import filterType from '@/constants/filterType'

    const props = defineProps({
        uuid: String,
        filters: Array,
    })

    const filterStore = useFilterStore()
    const { openQuickFilterModal, openFilterModal, withoutNestedFilter } = useFilters()

    const activeFilters = computed(() => {
        return filterStore.filtersForUuid(props.uuid) ?? {}
    })

    const activeFilterCount = computed(() => {
        return keys(activeFilters.value).length
    })

    const removeFilter = (filter, groupIds = []) => {
        filterStore.updateFilters(
            props.uuid,
            withoutNestedFilter(activeFilters.value, filter.id, groupIds),
        )
    }

    const addFilter = (filter, groupIds = []) => {
        openQuickFilterModal(props.uuid, filter, groupIds)
    }

    const getBarItems = (filter, groupIds = []) => {
        const value = get(activeFilters.value, [...groupIds, filter.id])
        let active = value !== undefined
        if (active && filter.type === filterType.select && filter.multiple && !value?.length) {
            active = false
        }

        const filterItem = {
            id: filter.id,
            title: filter.title,
            active,
            onClick: active ? () => removeFilter(filter, groupIds) : () => addFilter(filter, groupIds),
        }

        // Override the title for text filters
        if (filter.type === filterType.text) {
            if (!active) {
                return []
            }
            filterItem.title = value
        }

        // Don't show add button for group filters
        if (filter.type === filterType.group) {
            if (!active) {
                return []
            }
            return filter.filters.flatMap((childFilter) => {
                // Groups contain multiple filters
                return getBarItems(childFilter, [...groupIds, filter.id])
            })
        }

        // Add title for single-select filters
        if (filter.type === filterType.select && !filter.multiple && active) {
            filterItem.title = value
        }

        // Add multiple items for multi-select filters
        if (filter.type === filterType.select && filter.multiple && active) {
            return [
                {
                    ...filterItem,
                    count: value?.length,
                    onClick: () => addFilter(filter),
                },
            ]
        }

        return [filterItem]
    }

    const barItems = computed(() => {
        return flatMap(props.filters, (filter) => getBarItems(filter))
    })

    const handleFilters = () => {
        openFilterModal(props.uuid, props.filters)
    }
</script>
